import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { crudeApi } from '../../helpers/crude_api_helper';

// Generic adapter for the three ChangeRequest list endpoints — they share an
// identical request/response shape and differ only in URL and the name of the
// detail collection (detailReviewals / detailApprovals / detailMyList).
//
// Server-driven pagination, search and sorting (same pattern as AuditView).
// `sortField` must be PascalCase and within the API whitelist:
// Id, EntityId, EntityName, DataKey, ChangedType, Status, Created, CreatedBy.

const toPascalCase = field =>
  field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

const useChangeRequests = ({ endpoint, detailKey }) => {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: perPage };
      if (debouncedSearch) params.searchText = debouncedSearch;
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'asc' ? 'Asc' : 'Desc';
      }
      const result = await crudeApi.get(endpoint, params);
      const list = Array.isArray(result?.items) ? result.items : [];
      setItems(
        list.map(row => {
          const details = Array.isArray(row[detailKey]) ? row[detailKey] : [];
          return {
            ...row,
            // Normalized detail array (endpoint-specific key varies:
            // detailReviewals / detailApprovals / detailMyList) so the shared
            // list UI can render the expand panel without knowing detailKey.
            details,
            // "Total Changes" is the number of before/after field entries.
            totalChanges: details.length,
          };
        })
      );
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    endpoint,
    detailKey,
    page,
    perPage,
    debouncedSearch,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column, direction) => {
    setSortField(column.id ? toPascalCase(column.id) : null);
    setSortDirection(direction);
  };

  return {
    items,
    totalRows,
    loading,
    perPage,
    searchInput,
    setSearchInput,
    setPage,
    setPerPage,
    handleSort,
    refetch: fetchData,
  };
};

export default useChangeRequests;
